import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { useSnapshot } from 'valtio';
import {
  useBrandRefusedCheckoutsQuery,
  useBrandRulesQuery,
  useUpdateBrandRulesMutation,
} from '../../services/brandRulesService/__generated__/queries.generated';
import SwitchComponent from '../../components/switch';
import { Alert, Badge, Card, CardBody, CardHeader, LoadingBlock } from '../../components/ui';
import { toastActions } from '../../../store/toastStore';
import { authStore } from '../../../store/authStore';
import {
  bookingsOpenConfirm,
  bookingsOpenOf,
  bookingsStatusLabel,
  refusedCheckoutsText,
} from './bookingsOpenAdmin';

const Row = styled.label`
  ${tw`flex items-start justify-between gap-4`}
`;
const Text = styled.div`
  ${tw`min-w-0`}
`;
const Title = styled.div`
  ${tw`text-sm font-medium flex items-center gap-2`}
  color: var(--color-text-primary);
`;
const Hint = styled.div`
  ${tw`text-xs mt-1`}
  color: var(--color-text-secondary);
`;

function isStale(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
    ?.graphQLErrors;
  return Boolean(graphQLErrors?.some(e => e.extensions?.code === 'BRAND_RULES_STALE'));
}

/**
 * Spec 023: whether this site takes bookings yet. Closed, guests still see cars
 * and checkout but cannot pay. Only the platform owner switches it; brand admins
 * see the state.
 */
export function BookingsOpenSection({ brandName }: { brandName: string }) {
  const { user } = useSnapshot(authStore);
  const isPlatformOwner = Boolean((user as { isPlatformOwner?: boolean } | null)?.isPlatformOwner);
  const { data, loading, error, refetch } = useBrandRulesQuery({ fetchPolicy: 'network-only' });
  const refused = useBrandRefusedCheckoutsQuery({ fetchPolicy: 'no-cache', errorPolicy: 'ignore' });
  const [updateBrandRules, { loading: saving }] = useUpdateBrandRulesMutation({
    refetchQueries: ['BrandRulesHistory'],
  });

  const open = bookingsOpenOf(data?.brandRules?.bookingRules);
  const countText = refusedCheckoutsText(
    refused.data?.brandRules?.refusedCheckoutsSinceClosed,
    open,
  );

  const save = async (nextOpen: boolean) => {
    if (!data?.brandRules) return;
    try {
      await updateBrandRules({
        variables: {
          input: { version: data.brandRules.version, bookingRules: { bookingsOpen: nextOpen } },
        },
      });
      toast.success(
        nextOpen
          ? `Bookings are open on ${brandName}. Every server follows within about a minute.`
          : `Bookings are closed on ${brandName}. Every server follows within about a minute.`,
      );
      await Promise.all([refetch(), refused.refetch()]);
    } catch (e) {
      if (isStale(e)) {
        toast.error('Someone else changed these rules. The latest values are loaded; try again.');
        await refetch();
        return;
      }
      toast.error((e as Error).message || 'Could not save');
    }
  };

  const ask = (nextOpen: boolean) => {
    const confirm = bookingsOpenConfirm(nextOpen, brandName);
    toastActions.showToastAlert(
      confirm.title,
      confirm.body,
      '',
      () => void save(nextOpen),
      () => undefined,
      confirm.confirm,
      'Cancel',
    );
  };

  return (
    <Card>
      <CardHeader
        title="Bookings"
        description={`Whether guests can book on ${brandName}. While bookings are not open, guests still see cars, prices and the checkout page, but pressing Pay tells them bookings open soon and nothing is charged.`}
      />
      {loading && <LoadingBlock label="Loading bookings status" />}
      {error && (
        <CardBody>
          <Alert tone="error" title="Could not load bookings status">
            {error.message}
          </Alert>
        </CardBody>
      )}
      {!loading && !error && (
        <CardBody>
          <Row as={isPlatformOwner ? 'label' : 'div'} data-testid="bookings-open-row">
            <Text>
              <Title>
                Bookings
                <Badge tone={open ? 'success' : 'warning'} data-testid="bookings-open-status">
                  {bookingsStatusLabel(open)}
                </Badge>
              </Title>
              <Hint>
                {isPlatformOwner
                  ? 'Turn on when insurance, the Stripe account and support are ready.'
                  : 'Set by the platform owner.'}
              </Hint>
              {countText && <Hint data-testid="bookings-open-refused">{countText}</Hint>}
            </Text>
            {isPlatformOwner && (
              <SwitchComponent
                isActive={open}
                disabled={saving}
                ariaLabel="Bookings open"
                onChange={e => ask(e.target.checked)}
                inputTestId="bookings-open-switch"
              />
            )}
          </Row>
        </CardBody>
      )}
    </Card>
  );
}

export default BookingsOpenSection;
